import React from 'react';
import { Package, Download, ShoppingBag, CheckCircle, CreditCard, Check, ArrowRight } from 'lucide-react';
import type { BlockRenderProps } from './types';
import { EditableText } from './EditableText';

export const DigitalProductCardRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const title = (node.props?.title as string) || 'Physics Formula Booklet (Class 12 & JEE)';
  const description = (node.props?.description as string) || 'Comprehensive formula summaries, high-yield shortcuts, and solved example problems.';
  const price = (node.props?.price as number | string) ?? 299;
  const currency = (node.props?.currency as string) || 'INR';
  const currencySymbol = currency === 'USD' ? '$' : '₹';
  const fileType = (node.props?.fileType as string) || 'PDF';
  const badge = (node.props?.badge as string) || 'Instant Download';
  const thumbnail = (node.props?.thumbnail as string) || 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=600&auto=format&fit=crop&q=80';
  const buttonText = (node.props?.buttonText as string) || 'Get Instant Access';

  return (
    <div className="rounded-xl border border-border bg-card text-card-foreground shadow-sm overflow-hidden flex flex-col transition-all hover:shadow-md max-w-sm">
      <div className="relative h-44 w-full bg-muted overflow-hidden">
        <img
          src={thumbnail}
          alt={title}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        {badge && (
          <span className="absolute top-2.5 right-2.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-primary text-primary-foreground shadow">
            <EditableText
              as="span"
              value={badge}
              isEditor={isEditor}
              onChange={(val) => onUpdateProps?.(node.id, { badge: val })}
            />
          </span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium mb-1.5">
            <Package className="h-3.5 w-3.5" />
            <span>Format: {fileType}</span>
          </div>
          <EditableText
            as="h3"
            value={title}
            isEditor={isEditor}
            onChange={(val) => onUpdateProps?.(node.id, { title: val })}
            className="font-bold text-base text-foreground leading-snug mb-1.5 block"
          />
          <EditableText
            as="p"
            value={description}
            isEditor={isEditor}
            onChange={(val) => onUpdateProps?.(node.id, { description: val })}
            className="text-xs text-muted-foreground line-clamp-2 mb-4 leading-relaxed block"
            multiline
          />
        </div>
        <div className="pt-3 border-t border-border flex items-center justify-between">
          <div className="flex flex-col">
            <span className="text-[10px] text-muted-foreground uppercase font-bold tracking-wider">Price</span>
            <span className="text-lg font-extrabold text-primary">
              {currencySymbol}
              <EditableText
                as="span"
                value={String(price)}
                isEditor={isEditor}
                onChange={(val) => onUpdateProps?.(node.id, { price: val })}
              />
            </span>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:opacity-90 shadow-sm transition-opacity"
          >
            <Download className="h-3.5 w-3.5" />
            <EditableText
              as="span"
              value={buttonText}
              isEditor={isEditor}
              onChange={(val) => onUpdateProps?.(node.id, { buttonText: val })}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

export const DigitalProductListRenderer: React.FC<BlockRenderProps> = ({ node, children, isEditor, onUpdateProps }) => {
  const sectionTitle = (node.props?.sectionTitle as string) || 'Digital Study Materials & Downloads';

  return (
    <div className="py-6 w-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <EditableText
            as="h2"
            value={sectionTitle}
            isEditor={isEditor}
            onChange={(val) => onUpdateProps?.(node.id, { sectionTitle: val })}
            className="text-xl font-bold text-foreground block"
          />
          <p className="text-xs text-muted-foreground mt-0.5">Verified notes, test series, and instant download ebooks</p>
        </div>
        <span className="text-xs bg-muted px-2.5 py-1 rounded text-muted-foreground font-medium flex items-center gap-1">
          <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
          <span>Instant PDF Access</span>
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {children || (
          <div className="col-span-full p-8 border border-dashed border-border rounded-xl text-center text-sm text-muted-foreground">
            Drop Digital Product Cards inside this grid to build your digital study material shop.
          </div>
        )}
      </div>
    </div>
  );
};

export const StorefrontCheckoutBlockRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const buttonLabel = (node.props?.buttonLabel as string) || 'Enroll & Checkout Now';
  const priceDisplay = (node.props?.priceDisplay as string) || '₹4,999';

  return (
    <div className="p-4 rounded-xl border border-primary/20 bg-primary/5 flex flex-col sm:flex-row items-center justify-between gap-4 my-4">
      <div>
        <div className="text-xs font-semibold text-primary uppercase tracking-wider">Fast Checkout</div>
        <div className="text-sm text-muted-foreground">Immediate enrollment with UPI, Credit/Debit card, and Netbanking</div>
      </div>
      <button
        type="button"
        className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-primary text-primary-foreground font-semibold text-xs shadow hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
      >
        <span>
          <EditableText
            as="span"
            value={buttonLabel}
            isEditor={isEditor}
            onChange={(val) => onUpdateProps?.(node.id, { buttonLabel: val })}
          />
        </span>
        <span className="opacity-80">({priceDisplay})</span>
        <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
};

export const PricingTableRenderer: React.FC<BlockRenderProps> = ({ node, isEditor, onUpdateProps }) => {
  const headline = (node.props?.headline as string) || 'Admission Plans & Fee Tiers';
  const subheadline = (node.props?.subheadline as string) || 'Select the ideal package matching your preparation horizon and target examination.';

  const tiers = [
    {
      key: 'tier1',
      title: (node.props?.tier1_title as string) || 'Foundation (Class 9-10)',
      price: (node.props?.tier1_price as string) || '₹34,999',
      period: (node.props?.tier1_period as string) || '/ year',
      badge: (node.props?.tier1_badge as string) || 'Foundation',
      features: ((node.props?.tier1_features as string) || 'Board Syllabus Mastery, NTSE & Olympiad Prep, Weekly Chapter Quizzes, Printed Study Material')
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean),
      ctaText: (node.props?.tier1_ctaText as string) || 'Enroll in Foundation',
      ctaLink: (node.props?.tier1_ctaLink as string) || '#admissions',
      highlight: false,
    },
    {
      key: 'tier2',
      title: (node.props?.tier2_title as string) || 'Target Entrance (JEE/NEET)',
      price: (node.props?.tier2_price as string) || '₹59,999',
      period: (node.props?.tier2_period as string) || '/ year',
      badge: (node.props?.tier2_badge as string) || 'Most Popular',
      features: ((node.props?.tier2_features as string) || 'Ex-IITian Live Lectures, Daily Practice Problems (DPP), All-India Test Series, 1-on-1 Doubt Solving, AIIMS/JEE Question Bank')
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean),
      ctaText: (node.props?.tier2_ctaText as string) || 'Join Target Batch',
      ctaLink: (node.props?.tier2_ctaLink as string) || '#checkout',
      highlight: true,
    },
    {
      key: 'tier3',
      title: (node.props?.tier3_title as string) || 'Rankers Super 30',
      price: (node.props?.tier3_price as string) || '₹89,999',
      period: (node.props?.tier3_period as string) || '/ year',
      badge: (node.props?.tier3_badge as string) || 'Interview Required',
      features: ((node.props?.tier3_features as string) || 'Top Batch Faculty, Dedicated Personal Coach, Advanced Problem Solving, Full Test Analysis, Hostel & Library Access')
        .split(',')
        .map((f) => f.trim())
        .filter(Boolean),
      ctaText: (node.props?.tier3_ctaText as string) || 'Apply for Super 30',
      ctaLink: (node.props?.tier3_ctaLink as string) || '#admissions',
      highlight: false,
    },
  ];

  return (
    <div className="py-8 w-full text-center">
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-2">
        <CreditCard className="h-3.5 w-3.5" />
        <span>Admission Plans</span>
      </div>
      <EditableText
        as="h2"
        value={headline}
        isEditor={isEditor}
        onChange={(val) => onUpdateProps?.(node.id, { headline: val })}
        className="text-2xl md:text-3xl font-extrabold text-foreground tracking-tight mb-2 block"
      />
      <EditableText
        as="p"
        value={subheadline}
        isEditor={isEditor}
        onChange={(val) => onUpdateProps?.(node.id, { subheadline: val })}
        className="text-xs md:text-sm text-muted-foreground max-w-lg mx-auto mb-8 block"
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto text-left">
        {tiers.map((tier, idx) => (
          <div
            key={idx}
            className={`rounded-2xl p-6 flex flex-col justify-between transition-all ${
              tier.highlight
                ? 'bg-card border-2 border-primary shadow-xl ring-2 ring-primary/20 relative'
                : 'bg-card border border-border shadow-xs hover:border-primary/40'
            }`}
          >
            <div>
              {tier.badge && (
                <div className="flex justify-between items-center mb-3">
                  <span
                    className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full ${
                      tier.highlight
                        ? 'bg-primary text-primary-foreground shadow-xs'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <EditableText
                      as="span"
                      value={tier.badge}
                      isEditor={isEditor}
                      onChange={(val) => onUpdateProps?.(node.id, { [`${tier.key}_badge`]: val })}
                    />
                  </span>
                </div>
              )}
              <EditableText
                as="h3"
                value={tier.title}
                isEditor={isEditor}
                onChange={(val) => onUpdateProps?.(node.id, { [`${tier.key}_title`]: val })}
                className="font-bold text-lg text-foreground mb-1 block"
              />
              <div className="flex items-baseline gap-1 my-3">
                <span className="text-3xl font-black text-foreground">
                  <EditableText
                    as="span"
                    value={tier.price}
                    isEditor={isEditor}
                    onChange={(val) => onUpdateProps?.(node.id, { [`${tier.key}_price`]: val })}
                  />
                </span>
                <span className="text-xs text-muted-foreground">{tier.period}</span>
              </div>

              <div className="h-[1px] bg-border my-4" />

              <ul className="space-y-2.5 mb-6 text-xs text-muted-foreground">
                {tier.features.map((feat, fIdx) => (
                  <li key={fIdx} className="flex items-start gap-2">
                    <Check className="h-3.5 w-3.5 text-primary shrink-0 mt-0.5" />
                    <span>{feat}</span>
                  </li>
                ))}
              </ul>
            </div>

            <a
              href={tier.ctaLink}
              className={`w-full py-2.5 px-4 rounded-xl text-xs font-bold text-center transition-all inline-flex items-center justify-center gap-1.5 ${
                tier.highlight
                  ? 'bg-primary text-primary-foreground hover:opacity-90 shadow-sm'
                  : 'bg-muted hover:bg-muted/80 text-foreground'
              }`}
            >
              <EditableText
                as="span"
                value={tier.ctaText}
                isEditor={isEditor}
                onChange={(val) => onUpdateProps?.(node.id, { [`${tier.key}_ctaText`]: val })}
              />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
};
